import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useAccount, useChainId, useSwitchChain, useWalletClient } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Trash2 } from 'lucide-react';

import Head from "next/head";
import Image from "next/image";

import { Game, getHotGames, getBannerGames, getEnabledGames } from "../data/games";
import { NotificationProvider } from "../components/notification-provider";
import BinanceWalletButton from "../components/BinanceWalletButton";
import { useBinanceDetection } from "../hooks/useBinanceDetection";
import { usePlaysoutAPI } from "../hooks/usePlaysoutAPI";
import Toast from "../components/Toast";

// 全局变量：追踪钱包连接状态，跨页面导航保持
// 只有当用户在当前会话中主动连接钱包时才触发自动登录
let globalWasConnected: boolean | null = null;
let globalHasTriggeredAutoLogin = false;

// 重置全局状态的函数（disconnect 时调用）
export function resetAutoLoginState() {
  console.log('[resetAutoLoginState] Called! Before:', { globalWasConnected, globalHasTriggeredAutoLogin });
  globalWasConnected = null;
  globalHasTriggeredAutoLogin = false;
  console.log('[resetAutoLoginState] After:', { globalWasConnected, globalHasTriggeredAutoLogin });
}

export default function HomePage() {
  const router = useRouter();
  const { isConnected, address } = useAccount();
  const { shouldShowConnectWallet } = useBinanceDetection();
  const { isLoggedIn: isPlaysoutLoggedIn, login: playsoutLogin, isLoggingIn } = usePlaysoutAPI();
  const { data: walletClient } = useWalletClient();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();

  const [recentGames, setRecentGames] = useState<Game[]>([]);
  const [popularGames, setPopularGames] = useState<Game[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoginRequired, setShowLoginRequired] = useState(false);
  const [loginCancelled, setLoginCancelled] = useState(false);

  // 钱包连接后自动触发登录（仅在从 未连接→已连接 时触发，页面加载时已连接不触发）
  useEffect(() => {
    const isNowFullyConnected = !!(isConnected && address && walletClient);
    
    console.log('[HomePage] Auto-login effect running:', {
      isConnected,
      address: address ? `${address.slice(0, 6)}...` : null,
      hasWalletClient: !!walletClient,
      isNowFullyConnected,
      globalWasConnected,
      globalHasTriggeredAutoLogin,
      isPlaysoutLoggedIn,
      isLoggingIn,
      loginCancelled,
    });
    
    // 只有当之前是未连接状态（globalWasConnected === false），现在变成已连接，才是"刚连接"
    // globalWasConnected === null 表示首次加载/刚重置，不触发自动登录
    const isJustConnected = globalWasConnected === false && isNowFullyConnected;
    
    console.log('[HomePage] isJustConnected:', isJustConnected, '(requires globalWasConnected===false && isNowFullyConnected===true)');
    
    // 更新全局状态用于下次比较
    const previousGlobalWasConnected = globalWasConnected;
    
    // 关键修复：当 globalWasConnected === null 时，只有真正连接成功才设为 true
    // 不要在 null 状态时设为 false，否则 wagmi 异步加载时会误触发
    if (globalWasConnected === null) {
      if (isNowFullyConnected) {
        globalWasConnected = true; // 页面加载时钱包已连接
        console.log('[HomePage] First connection seen, setting globalWasConnected to true (no auto-login)');
      }
      // 如果未连接，保持 null，等待真正的连接事件
    } else {
      // 已有之前的状态，正常更新
      globalWasConnected = isNowFullyConnected;
    }
    console.log('[HomePage] Updated globalWasConnected:', previousGlobalWasConnected, '->', globalWasConnected);
    
    if (isJustConnected && !isPlaysoutLoggedIn && !isLoggingIn && !loginCancelled && !globalHasTriggeredAutoLogin) {
      globalHasTriggeredAutoLogin = true;
      console.log('[HomePage] ✅ TRIGGERING AUTO-LOGIN!');
      playsoutLogin().then((success) => {
        console.log('[HomePage] Auto-login result:', success);
        if (!success) {
          // 用户取消或登录失败，不再自动重试
          setLoginCancelled(true);
        }
      });
    } else if (isNowFullyConnected && !isPlaysoutLoggedIn) {
      console.log('[HomePage] ❌ NOT triggering auto-login. Reasons:', {
        'isJustConnected': isJustConnected,
        'globalWasConnected was': previousGlobalWasConnected,
        'globalHasTriggeredAutoLogin': globalHasTriggeredAutoLogin,
        'isLoggingIn': isLoggingIn,
        'loginCancelled': loginCancelled,
      });
    }
  }, [isConnected, address, walletClient, isPlaysoutLoggedIn, isLoggingIn, loginCancelled, playsoutLogin]);

  // 钱包断开连接时重置状态
  useEffect(() => {
    console.log('[HomePage] Disconnect effect running, isConnected:', isConnected);
    if (!isConnected) {
      console.log('[HomePage] 🔴 Wallet disconnected! Resetting state...');
      setLoginCancelled(false);
      // 重置全局状态，这样下次连接才会被视为"刚连接"
      globalWasConnected = false;
      globalHasTriggeredAutoLogin = false;
      console.log('[HomePage] State after disconnect:', { globalWasConnected, globalHasTriggeredAutoLogin });
    }
  }, [isConnected]);

  // 从localStorage加载最近游戏
  useEffect(() => {
    // Popular games (hot games - now includes all main games)
    loadRecentGames();
    setPopularGames(getHotGames());
    // Simulate loading delay to show skeleton
    setTimeout(() => setIsLoading(false), 300);
  }, []);

  // 将切链与跳转串行化，避免二次点击和时序问题
  const ensureBscAndNavigate = async (game: Game) => {
    const targetChainId = 56;
    const current = walletClient?.chain?.id ?? chainId;
    if (current !== targetChainId) {
      await switchChain({ chainId: targetChainId });
    }
    setTimeout(() => {
      addToRecentGames(game.id);
    }, 500);
    router.push(`/games/${game.id}`);
  };

  const loadRecentGames = () => {
    const recentGameIds = JSON.parse(localStorage.getItem('recent-games') || '[]');
    const enabledGames = getEnabledGames();
    const recentGames = recentGameIds
      .map((id: string) => enabledGames.find(game => game.id === id))
      .filter((game: Game | undefined): game is Game => game !== undefined);
    setRecentGames(recentGames);
  };

  // 添加游戏到最近游戏列表（只更新数据，不更新UI，避免跳转前闪烁）
  const addToRecentGames = (gameId: string) => {
    try {
      const recentGameIds = JSON.parse(localStorage.getItem('recent-games') || '[]');

      // 移除已存在的相同游戏ID（如果有的话）
      const filteredIds = recentGameIds.filter((id: string) => id !== gameId);

      // 将新游戏ID添加到开头
      const newRecentIds = [gameId, ...filteredIds].slice(0, 6); // 只保留最近6个

      // 保存到localStorage（UI会在返回首页时通过loadRecentGames更新）
      localStorage.setItem('recent-games', JSON.stringify(newRecentIds));
    } catch (error) {
      console.error('Error saving recent game:', error);
    }
  };

  // 从最近游戏列表中删除游戏
  const removeFromRecentGames = (gameId: string) => {
    try {
      const recentGameIds = JSON.parse(localStorage.getItem('recent-games') || '[]');
      const newRecentIds = recentGameIds.filter((id: string) => id !== gameId);

      // 保存到localStorage
      localStorage.setItem('recent-games', JSON.stringify(newRecentIds));

      // 更新状态
      const enabledGames = getEnabledGames();
      const newRecentGames = newRecentIds
        .map((id: string) => enabledGames.find(game => game.id === id))
        .filter((game: Game | undefined): game is Game => game !== undefined);
      setRecentGames(newRecentGames);
    } catch (error) {
      console.error('Error removing recent game:', error);
    }
  };

  const handleGameClick = async (game: Game) => {
    // 检查是否已连接钱包且已登录
    if (!isConnected || !isPlaysoutLoggedIn || isLoggingIn) {
      console.log('[HomePage] User not connected or not logged in, showing notification');
      setShowLoginRequired(true);
      setTimeout(() => setShowLoginRequired(false), 3000);
      return;
    }

    try {
      await ensureBscAndNavigate(game);
    } catch (e) {
      console.error('Switch to BSC before enter game failed:', e);
      // 可在此处提示用户手动切换到BSC后再试
    }
  };

  // 移除未使用的函数

  return (
    <NotificationProvider>
      <Head>
        <title>Playsout Game Platform - Discover Amazing Games</title>
        <meta name="description" content="Explore the hottest casual games" />
      </Head>

      <main className="min-h-screen">
        <div className="mobile-container bg-black rounded-md shadow-xl my-1">
          {/* Toast - centered within mobile-container */}
          <Toast 
            show={showLoginRequired} 
            message="Please login first to play games"
          />
          
          {/* Header */}
          <header className="flex items-center justify-between py-4 px-2">
            <div className="flex items-center">
              <div className="w-[40px] h-8 relative bg-transparent">
                <Image
                  src="/images/logo-white.png"
                  alt="Logo"
                  fill
                  className="object-contain"
                  priority
                  sizes="40px"
                  placeholder="empty"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Binance Wallet Button - shows on mobile when not in Binance Wallet */}
              <BinanceWalletButton />

              {/* Connect Wallet / Login / Profile Button */}
              {isConnected ? (
                isPlaysoutLoggedIn ? (
                  <button
                    onClick={() => router.push('/profile')}
                    className="flex items-center gap-2 bg-game-accent hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Profile
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setLoginCancelled(false); // 重置取消状态，允许手动登录
                      playsoutLogin();
                    }}
                    disabled={isLoggingIn}
                    className="flex items-center gap-2 bg-game-accent hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors text-sm disabled:opacity-50"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    {isLoggingIn ? 'Signing...' : 'Login'}
                  </button>
                )
              ) : shouldShowConnectWallet ? (
                <ConnectButton />
              ) : null}
            </div>
          </header>

          

          <div className="px-4 pb-4">
            {/* Clickable Banner */}
            <section className="mb-8">
              {(() => {
                // Get enabled banner games
                const bannerGames = getBannerGames();
                const bannerGame = bannerGames.length > 0 ? bannerGames[0] : null;

                if (!bannerGame) {
                  return null; // No banner to show if no enabled banner games
                }

                return (
                  <div
                    onClick={() => handleGameClick(bannerGame)}
                    className="relative w-full rounded-2xl overflow-hidden cursor-pointer border border-game-card"
                  >
                    <Image
                      src={bannerGame.image}
                      alt={`${bannerGame.title} banner`}
                      width={0}
                      height={0}
                      className="w-full h-auto object-cover select-none"
                      priority
                      sizes="100vw"
                    />
                  </div>
                );
              })()}
            </section>

            {/* Recently Played - 只在有数据时显示 */}
            {recentGames.length > 0 && (
              <section className="mb-8">
                <h2 className="text-game-text text-lg font-semibold mb-4">Recently Played</h2>
                <div className="recently-played-scroll">
                  {recentGames.map((game) => (
                    <div key={game.id} className="recently-played-card">
                      <GameCard
                        game={game}
                        onClick={() => handleGameClick(game)}
                        showCategory={false}
                        showDelete={true}
                        onDelete={() => removeFromRecentGames(game.id)}
                        showBadges={false}
                      />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Popular Games */}
            <section>
              <h2 className="text-game-text text-lg font-semibold mb-4">Popular Games</h2>
              <div className="grid grid-cols-2 gap-2">
                {isLoading ? (
                  // Show 6 skeleton cards while loading
                  Array.from({ length: 6 }).map((_, index) => (
                    <GameCardSkeleton key={index} showCategory={true} />
                  ))
                ) : (
                  popularGames.map((game) => (
                    <GameCard
                      key={game.id}
                      game={game}
                      onClick={() => handleGameClick(game)}
                    />
                  ))
                )}
              </div>
            </section>
            <div className="mt-6 text-center text-game-text-secondary text-sm">
              No more games
            </div>
          </div>

          {/* Bottom Navigation hidden as requested */}
        </div>
      </main>
    </NotificationProvider>
  );
}

interface GameCardProps {
  game: Game;
  onClick: () => void;
  showCategory?: boolean;
  showDelete?: boolean;
  onDelete?: () => void;
  showBadges?: boolean;
}

function GameCard({ game, onClick, showCategory = true, showDelete = false, onDelete, showBadges = true }: GameCardProps) {
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation(); // 防止触发游戏点击事件
    if (onDelete) {
      onDelete();
    }
  };

  return (
    <div onClick={onClick} className="game-card">
      <div className="game-image">
        <Image
          src={game.image}
          alt={game.title}
          fill
          className="object-cover select-none"
          sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
          draggable={false}
        />
      </div>

      <div className="game-info">
        <div className="title-row">
          <h3 className="game-title">{game.title}</h3>
          {showBadges && game.isNew && (
            <div className="badge new">
              NEW
            </div>
          )}
          {showBadges && game.isHot && (
            <div className="badge hot">
              HOT
            </div>
          )}
          {showDelete && onDelete && (
            <button
              onClick={handleDelete}
              className="delete-icon-button"
              aria-label="Remove from recent games"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
        {showCategory && (
          <>
            <div className="divider"></div>
            <p className="game-category">{game.category}</p>
          </>
        )}
      </div>
    </div>
  );
}

interface GameCardSkeletonProps {
  showCategory?: boolean;
}

function GameCardSkeleton({ showCategory = true }: GameCardSkeletonProps) {
  return (
    <div className="game-card animate-pulse">
      {/* Image skeleton */}
      <div className="game-image bg-gray-700"></div>

      {/* Info skeleton */}
      <div className="game-info">
        <div className="title-row">
          {/* Title skeleton */}
          <div className="h-4 bg-gray-700 rounded w-3/4"></div>
          {/* Badge skeleton */}
          <div className="h-4 bg-gray-700 rounded w-10"></div>
        </div>
        {showCategory && (
          <>
            <div className="divider bg-gray-700"></div>
            {/* Category skeleton */}
            <div className="h-3 bg-gray-700 rounded w-1/2"></div>
          </>
        )}
      </div>
    </div>
  );
}
